/**
 * Multiplayer UI adapters: the seam between multiplayer.js (which knows
 * Photon and the backend) and the React HUD surfaces (which know the
 * stores). Every export keeps its historical signature so multiplayer.js
 * and videochat.js are untouched; the bodies are now store writes, handler
 * registrations, and one-time React mounts.
 *
 * The surfaces themselves live in src/ui/react/ (PeoplePanel, HostBar,
 * SelfView, FollowBanner, ModerationNotice, RemovedOverlay, LeftOverlay,
 * MovementModeToggle) and read roomStore / sessionStore / hudStore. The
 * meeting toolbar (src/ui/hud/meetingToolbar.js) is a separate vanilla-DOM
 * surface wired directly by multiplayer.js; this module only forwards the
 * canonical media state to it so the two never drift.
 */

import { createElement } from 'react';
import { registerHandlers, registerFollowStop } from './react/handlerRegistry';
import {
  setMovementToggleVisible,
  setFollowBanner,
  setRemovedOverlay,
  setLeftOverlay,
  showNotice,
  setMediaState,
} from '../state/hudStore';
import { mountReactSurface, mountReactBodySurface } from './react/mountRegion';
import { MovementModeToggle } from './react/MovementModeToggle';
import { FollowBanner } from './react/FollowBanner';
import { SelfView } from './react/SelfView';
import { HostBar, HOST_BAR_MOUNT } from './react/HostBar';
import { ModerationNotice } from './react/ModerationNotice';
import { RemovedOverlay } from './react/RemovedOverlay';
import { LeftOverlay } from './react/LeftOverlay';
import { PeoplePanel } from './react/PeoplePanel';
import { networkQualityColor } from './react/networkDotShared';
import { exitCaptureMode } from './captureMode.js';

// --- Handler registration ----------------------------------------------------
// The React surfaces read these from the handler registry at event time.

export function registerModerationHandlers(handlers) {
  registerHandlers({ moderation: handlers });
}

export function registerLobbyHandlers(handlers) {
  registerHandlers({ lobby: handlers });
}

export function registerPresenterHandlers(handlers) {
  registerHandlers({ presenter: handlers });
}

export function registerRoleHandlers(handlers) {
  registerHandlers({ role: handlers });
}

// --- Legacy string helper (videoGrid tile badges) ----------------------------

// Colored connection dot from an Agora quality score (1-2 good, 3-4 fair,
// 5-6 poor; null/0 = unknown). The React People panel derives the same dot
// from networkDotShared.ts, so the two can never disagree.
export function networkDot(score) {
  const { color, label } = networkQualityColor(score);
  return `<span title="connection: ${label}" style="display:inline-block;width:9px;height:9px;border-radius:50%;background:${color};margin-left:6px;vertical-align:middle;"></span>`;
}

// --- Mounts ------------------------------------------------------------------

// The React host bar derives its action set and paint state from the stores,
// so it mounts once and re-gates itself (no syncHostBar, no paint helpers).
let hostBarMount = null;
function ensureHostBarMounted() {
  if (hostBarMount) return;
  hostBarMount = mountReactSurface(HOST_BAR_MOUNT.region, createElement(HostBar), {
    order: HOST_BAR_MOUNT.order,
  });
}

let peoplePanelMount = null;
function ensurePeoplePanelMounted() {
  if (peoplePanelMount) return;
  peoplePanelMount = mountReactSurface('top-left', createElement(PeoplePanel));
}

/**
 * Event-driven roster update from multiplayer.js (joins/leaves/role/level
 * changes. The coordinator publishes the canonical roster first.
 * @param {Record<string, Object>} avatars
 */
export function updateUserList(avatars) {
  ensurePeoplePanelMounted();
  ensureHostBarMounted();
  // Bridge for videochat.js (and the multiplayerVideo e2e spec).
  window.AVATARS = avatars;
}

// --- Overlay + control adapters ----------------------------------------------

// Persistent banner shown to a participant while they follow the presenter.
// Moving (WASD/arrows) also ends the follow; the button is the discoverable way.
let followBannerMount = null;
export function showFollowBanner(presenterName, onStop) {
  if (!followBannerMount) {
    followBannerMount = mountReactSurface('bottom-center', createElement(FollowBanner));
  }
  registerFollowStop(onStop);
  setFollowBanner({ presenterName });
}

export function hideFollowBanner() {
  setFollowBanner(null);
  registerFollowStop(null);
}

// Movement-mode toggle: persistent segmented control to flip between Follow Me
// (auto-join the presenter's tour) and Free Movement. Created only when the
// user opts into Follow Me in the welcome modal; stays for the session so they
// can switch back and forth. State lives in movementMode.js.
let movementToggleMount = null;
export function showMovementModeToggle() {
  if (!movementToggleMount) {
    movementToggleMount = mountReactSurface('bottom-center', createElement(MovementModeToggle));
  }
  setMovementToggleVisible(true);
}

// Transient toast for moderation feedback ("Muted Maya", "Room locked", or a
// moderation command applied to the local user).
let noticeMount = null;
export function showModerationNotice(text, isError = false) {
  if (!noticeMount) {
    noticeMount = mountReactBodySurface(createElement(ModerationNotice));
  }
  showNotice(text, isError);
}

// Full-screen terminal state shown to a participant the host removed. There is
// no way back into the session from here (the local session token has been
// cleared); a new invite is required.
let removedOverlayMount = null;
export function showRemovedOverlay() {
  exitCaptureMode();
  if (!removedOverlayMount) {
    removedOverlayMount = mountReactBodySurface(createElement(RemovedOverlay));
  }
  setRemovedOverlay(true);
}

// Full-screen terminal state after a voluntary Leave (the meeting toolbar's
// red button). Unlike the removed overlay there IS a way back - Rejoin
// reloads and goes back through the normal entry gate.
let leftOverlayMount = null;
export function showLeftOverlay() {
  exitCaptureMode();
  if (!leftOverlayMount) {
    leftOverlayMount = mountReactBodySurface(createElement(LeftOverlay));
  }
  setLeftOverlay(true);
}

// Top-right self-view: the local Agora camera tile. Hidden entirely for
// observers (who can't publish). The tile element (#local-video) is driven by
// videochat.js. Mic and camera controls live exclusively in the meeting
// toolbar - see SelfView.tsx for the tile contract.
let selfViewMount = null;
export function createSelfView() {
  if (!selfViewMount) {
    selfViewMount = mountReactSurface('top-right', createElement(SelfView));
  }
}

/**
 * Sync canonical media state (join, toggles, moderation-forced changes) to
 * every surface that paints it: the meeting toolbar's mic/cam buttons and
 * the stores behind the React HUD (the People panel's self row).
 */
export function updateMediaControlsState(mediaState) {
  if (!mediaState) return;
  setMediaState({ mic: !!mediaState.mic, camera: !!mediaState.camera });
}
